/* Native cart continuation + opt-in Care+. No global add-to-cart interception. */
(() => {
  'use strict';
  const configEl = document.getElementById('pascucci-care-config');
  if (!configEl) return;
  const config = JSON.parse(configEl.textContent);
  const root = config.root.replace(/\/?$/, '/');
  const dialog = document.getElementById('pascucci-care');
  const observe = (monitor, name, data) => {
    try { monitor?.record(name, data); } catch (_) { /* Analytics cannot interrupt commerce. */ }
  };
  const monitorCall = (monitor, name, data) => {
    try { monitor?.[name](data); } catch (_) { /* Optional monitoring only. */ }
  };
  let offering = false;
  let syncQueue = Promise.resolve();
  let offerProduct = null;
  let hasCare = config.hasCare === true;
  let cleanupPending = false;
  const eligible = item => config.eligible.includes(item.handle);
  const isCare = item => item.handle === config.handle || item.sku === config.sku;
  const machineName = handle => handle === 'starter-kit' ? 'Starter Kit' : 'Coffee Machine';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: config.currency || 'USD'
  }).format(cents / 100);

  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(body ? 8000 : 4500)
    });
    if (!response.ok) throw new Error(`Care+ ${path} unavailable (${response.status})`);
    return response.json();
  }

  // Allocate by machine handle. Properties keep Starter Kit and standalone coverage separate.
  // Key-based updates preserve every unrelated line, quantity, discount and selling plan.
  function postPurchaseAllowance(cart) {
    const order = String(cart.attributes?.['Care+ Starter Kit order'] || '');
    const timestamp = Date.parse(cart.attributes?.['Care+ Starter Kit date'] || '');
    const age = Date.now() - timestamp;
    return /^\d+$/.test(order) && Number.isFinite(age) && age >= 0 && age < 14 * 86400000 ? 1 : 0;
  }

  function excessUpdates(cart) {
    const capacity = Object.fromEntries(config.eligible.map(handle => [handle, 0]));
    for (const item of cart.items) if (eligible(item)) capacity[item.handle] += item.quantity;
    const updates = {};
    const plans = cart.items.filter(isCare);
    const associated = plans.filter(item => config.eligible.includes(item.properties?._care_for));
    const unassociated = plans.filter(item => !associated.includes(item));
    for (const item of associated) {
      const handle = item.properties._care_for;
      const quantity = Math.min(item.quantity, capacity[handle]);
      capacity[handle] -= quantity;
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    let remaining = Object.values(capacity).reduce((sum, qty) => sum + qty, 0) + postPurchaseAllowance(cart);
    for (const item of unassociated) {
      const quantity = Math.min(item.quantity, remaining);
      remaining -= quantity;
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    return updates;
  }

  function reconcileCart() {
    // Always read fresh state rather than a delayed mutation response.
    const work = syncQueue.catch(() => {}).then(async () => {
      let cart = await request('cart.js');
      const updates = excessUpdates(cart);
      if (Object.keys(updates).length) cart = await request('cart/update.js', { updates });
      return cart;
    });
    syncQueue = work;
    return work;
  }

  function refreshViews(cart) {
    const theme = window.PXUTheme;
    if (theme?.autoTreeCart?.refreshViews) return theme.autoTreeCart.refreshViews(cart);
    theme?.jsAjaxCart?.updateView();
    document.querySelectorAll('[data-bind="itemCount"]').forEach(el => { el.textContent = cart.item_count; });
    return Promise.resolve();
  }

  async function loadProduct() {
    const product = await request(`products/${encodeURIComponent(config.handle)}.js`);
    // Use the merchant's actual product/variant; never select another plan or a selling plan.
    const variant = product.variants?.find(v => v.sku === config.sku && v.available);
    if (product.handle !== config.handle || product.title !== config.title || !variant || variant.price <= 0 || product.requires_selling_plan) {
      throw new Error('Care+ is unavailable');
    }
    return { product, variant };
  }

  function remainingFor(cart, handle) {
    const machines = cart.items.filter(item => item.handle === handle).reduce((sum, item) => sum + item.quantity, 0);
    const assigned = cart.items.filter(item => isCare(item) && item.properties?._care_for === handle)
      .reduce((sum, item) => sum + item.quantity, 0);
    // Unassociated Care+ also consumes capacity, preventing direct product-page duplicates.
    const totalMachines = cart.items.filter(eligible).reduce((sum, item) => sum + item.quantity, 0);
    const totalPlans = cart.items.filter(isCare).reduce((sum, item) => sum + item.quantity, 0);
    return Math.max(0, Math.min(machines - assigned, totalMachines - totalPlans));
  }

  function showOffer(offer, detail, quantity, monitor) {
    return new Promise(resolve => {
      const accept = dialog.querySelector('[data-care-accept]');
      const dismissControls = [...dialog.querySelectorAll('[data-care-decline]')];
      const error = dialog.querySelector('[data-care-error]');
      const previousFocus = detail.trigger || detail.form.querySelector('.button--add-to-cart') || document.activeElement;
      const quantityNote = dialog.querySelector('[data-care-quantity]');
      let finished = false;
      let busy = false;
      let telemetryAddedQuantity = 0;
      const handle = config.pageHandle;
      dialog.querySelector('[data-care-machine]').textContent = machineName(handle);
      dialog.querySelectorAll('[data-care-price]').forEach(el => { el.textContent = money(offer.variant.price); });
      if (quantity > 1) {
        accept.textContent = `Add ${quantity} Care+ plans & checkout — ${money(quantity * offer.variant.price)}`;
        quantityNote.textContent = `${quantity} machines · ${money(offer.variant.price)} per machine · ${money(quantity * offer.variant.price)} total`;
      } else {
        accept.textContent = `Add Care+ & checkout — ${money(offer.variant.price)}`;
      }
      quantityNote.hidden = quantity <= 1;
      error.hidden = true;
      accept.disabled = false;
      accept.removeAttribute('aria-busy');
      dismissControls.forEach(button => { button.disabled = false; });
      dialog.querySelector('.pascucci-care__decline').textContent = 'Checkout without Care+';

      function finish(decline, checkout = true, reason = decline ? 'native_close' : 'accepted') {
        if (finished) return;
        finished = true;
        if (decline) observe(monitor, 'offer_dismissed', { reason });
        monitorCall(monitor, 'finish', reason);
        dialog.close();
        document.documentElement.classList.remove('care-dialog-open');
        dialog.removeEventListener('click', click);
        dialog.removeEventListener('cancel', cancel);
        dialog.removeEventListener('close', closed);
        dialog.removeEventListener('keydown', trapFocus);
        previousFocus?.focus({ preventScroll: true });
        resolve({ checkout, stayOnProduct: reason === 'close_x' });
      }
      const cancel = event => { event.preventDefault(); if (!busy) finish(true, true, 'escape'); };
      const closed = () => finish(true);
      function trapFocus(event) {
        if (event.key !== 'Tab') return;
        const controls = [...dialog.querySelectorAll('button:not(:disabled), a[href]')]
          .filter(el => el.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement.id === 'pascucci-care-title')) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      async function addProtection() {
        // Web Locks serializes accept across tabs where supported. Double-clicks are
        // guarded synchronously as well; ambiguous POST failures are never retried.
        const add = async () => {
          const cart = await request('cart.js');
          const count = Math.min(quantity, remainingFor(cart, handle));
          if (!count) return cart;
          telemetryAddedQuantity = count;
          const currentOffer = await loadProduct();
          if (currentOffer.variant.id !== offer.variant.id || currentOffer.variant.price !== offer.variant.price) {
            throw new Error('Care+ offer changed');
          }
          await request('cart/add.js', { items: [{
            id: currentOffer.variant.id, quantity: count,
            properties: { _care_for: handle, Protects: machineName(handle) }
          }] });
          hasCare = true;
          return reconcileCart();
        };
        return navigator.locks ? navigator.locks.request('pascucci-care-add', add) : add();
      }
      async function click(event) {
        if (event.target.closest('[data-care-decline]')) {
          if (!busy) {
            const closeX = !!event.target.closest('.pascucci-care__close');
            finish(true, !closeX, closeX ? 'close_x' : 'decline_button');
          }
          return;
        }
        if (event.target === dialog) {
          if (busy) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) finish(true, true, 'backdrop');
          return;
        }
        if (!event.target.closest('[data-care-accept]') || busy || finished) return;
        busy = true;
        observe(monitor, 'accept_clicked');
        accept.disabled = true;
        // Keep the accepted transaction atomic: a late response must never add a
        // plan after a simultaneous "without Care+" decision has navigated away.
        dismissControls.forEach(button => { button.disabled = true; });
        accept.setAttribute('aria-busy', 'true');
        try {
          const cart = await addProtection();
          observe(monitor, 'protection_confirmed', { quantity: telemetryAddedQuantity, reason: telemetryAddedQuantity ? 'added' : 'concurrent_coverage' });
          await refreshViews(cart);
          finish(false);
        } catch (_) {
          // A timed-out POST can have succeeded. Do not send a second add request.
          observe(monitor, 'protection_unconfirmed', { reason: 'add_unconfirmed' });
          error.textContent = 'We couldn’t confirm Care+. Your machine is safely in the cart. Review your items at checkout.';
          error.hidden = false;
          busy = false;
          dismissControls.forEach(button => { button.disabled = false; });
          accept.removeAttribute('aria-busy');
          dialog.querySelector('.pascucci-care__decline').textContent = 'Continue to checkout';
          dialog.querySelector('.pascucci-care__decline').focus();
        }
      }
      dialog.addEventListener('click', click);
      dialog.addEventListener('cancel', cancel);
      dialog.addEventListener('close', closed);
      dialog.addEventListener('keydown', trapFocus);
      try {
        dialog.showModal();
        monitorCall(monitor, 'shown');
        document.documentElement.classList.add('care-dialog-open');
        dialog.querySelector('#pascucci-care-title').focus({ preventScroll: true });
      } catch (_) { observe(monitor, 'offer_error', { reason: 'dialog_error' }); finish(false, false); }
    });
  }

  // Successful regular/floating PDP adds and custom Buy Now share this continuation.
  // Native wallet checkout, quick shops and article/collection buttons stay separate.
  document.addEventListener('pascucci:machine-added', event => {
    const detail = event.detail;
    if (config.enabled !== true || !dialog || typeof dialog.showModal !== 'function' || offering ||
        !config.eligible.includes(config.pageHandle) || !detail?.form?.closest('.pdp-hero-overlay, .sticky-atc') ||
        location.pathname.replace(/\/$/, '') !== `${root}products/${config.pageHandle}` ||
        !detail.product?.handle || detail.product.handle !== config.pageHandle) return;
    event.preventDefault();
    offering = true;
    let monitor = null;
    try { monitor = window.PUICareAnalytics?.begin(config.pageHandle, detail.quantity); } catch (_) { /* Optional. */ }
    (async () => {
      let decision = { checkout: false, stayOnProduct: false };
      try {
        const [offer, cart] = await Promise.all([offerProduct || loadProduct(), request('cart.js')]);
        monitorCall(monitor, 'cart', cart);
        const quantity = remainingFor(cart, config.pageHandle);
        if (quantity) decision = await showOffer(offer, detail, quantity, monitor);
        else observe(monitor, 'offer_skipped', { reason: 'already_covered' });
      } catch (error) {
        observe(monitor, 'offer_error', { reason: 'dependency_error' });
        // A local diagnostic event lets theme QA identify a failed dependency.
        document.dispatchEvent(new CustomEvent('pascucci:care-unavailable', { detail: { reason: error.message } }));
      }
      finally {
        offering = false;
        if (decision.checkout) observe(monitor, 'checkout_redirect');
        monitorCall(monitor, 'finish');
        detail.resume(decision.checkout ? { checkoutUrl: root + 'checkout' } : decision.stayOnProduct ? { stayOnProduct: true } : undefined);
        if (decision.stayOnProduct) {
          // The successful-add continuation has now re-enabled the initiating button.
          const trigger = detail.trigger || detail.form.querySelector('.button--add-to-cart');
          trigger?.focus({ preventScroll: true });
        }
        if (decision.checkout || decision.stayOnProduct) return;
        requestAnimationFrame(() => {
          const cartTitle = document.querySelector('[data-ajax-cart-drawer].is-visible .ajax-cart__cart-title');
          if (cartTitle) {
            cartTitle.tabIndex = -1;
            cartTitle.focus({ preventScroll: true });
          }
        });
      }
    })();
  });

  // Prefetch only on the two eligible PDPs. Failure is intentionally silent.
  if (config.enabled === true && dialog && config.eligible.includes(config.pageHandle)) {
    offerProduct = loadProduct().catch(() => null);
    offerProduct.then(offer => { if (!offer) offerProduct = null; });
  }

  // Housekeeping, never an upsell: native cart changes may happen on any page.
  // Let existing success callbacks render the reconciled cart and subtotal.
  if (window.jQuery) window.jQuery.ajaxPrefilter(options => {
    if (!/\/cart\/(change|update|clear)(\.js)?(?:\?|$)/.test(options.url || '')) return;
    const success = options.success;
    options.success = function (cart, ...args) {
      const context = this;
      hasCare = cart?.items?.some(isCare) || false;
      if (!hasCare || !Object.keys(excessUpdates(cart)).length) return success?.call(context, cart, ...args);
      reconcileCart().then(fresh => {
        success?.call(context, fresh, ...args);
        return refreshViews(fresh);
      }).catch(() => success?.call(context, cart, ...args));
    };
  });

  // Also handles restored tabs and existing orphan plans when entering the cart.
  const cleanup = () => {
    if (offering || !hasCare || cleanupPending) return;
    cleanupPending = true;
    request('cart.js').then(async cart => {
      if (Object.keys(excessUpdates(cart)).length) await refreshViews(await reconcileCart());
    }).catch(() => {}).finally(() => { cleanupPending = false; });
  };
  document.addEventListener('cart:updated', cleanup);
  window.addEventListener('pageshow', cleanup);
  configEl.dataset.ready = 'true';
})();
